import { BellRing, CircleCheck, KeyRound, Laptop, LogOut, MessageCircle, ShieldCheck, Smartphone } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiError, type ApiClient, type GoogleAccountStatus, type InboxNotification, type MfaSetup } from '@despachador/api-client';
import { AUTH_ERRORS } from '@despachador/contracts/auth-errors';
import { PASSWORD_RULES } from '@despachador/domain';
import { dateTime, Field, Loadable, Modal, Notice, Spinner, useAction, useAsync, useConfirm, useNow, useToast } from '../index';
import { goToGoogle, saveReturnPath } from './google-return';
import { AuthenticatorSetup, authMessage, CodeInput, countdown, errorCode, NewPassword, RecoveryCodes, retryAfterSeconds, waitText } from './pieces';
import { REAUTH_CANCELLED, useReauth } from './Reauth';

/** One of the account's open sessions (`GET /auth/sessions`). */
export interface OwnSession {
  id: string;
  audience: string;
  current: boolean;
  ip: string | null;
  user_agent: string | null;
  device_label: string | null;
  platform: string | null;
  created_at: string;
  last_used_at: string;
}

const APP_NAME: Record<string, string> = { admin: 'Panel', driver: 'App del conductor' };

/** A date inside a sentence: without the trailing dot of some locales. */
const when = (v: string) => dateTime(v).replace(/[.]$/, '');

/** «Chrome en Windows»: the label of the device, or what its browser says. */
export function deviceName(s: OwnSession) {
  if (s.device_label) return s.device_label;
  const ua = s.user_agent ?? '';
  const os = /Android/i.test(ua) ? 'Android' : /iPhone|iPad/i.test(ua) ? 'iPhone' : /Windows/i.test(ua) ? 'Windows' : /Mac OS/i.test(ua) ? 'Mac' : /Linux/i.test(ua) ? 'Linux' : '';
  const browser = /Edg\//.test(ua) ? 'Edge' : /Chrome\//.test(ua) ? 'Chrome' : /Firefox\//.test(ua) ? 'Firefox' : /Safari\//.test(ua) ? 'Safari' : '';
  return [browser, os].filter(Boolean).join(' en ') || 'Dispositivo';
}

/** "tu otra sesión" / "tus otras 3 sesiones": how many other sessions an operation closed. */
function otherSessions(n: number): string {
  if (n <= 0) return 'No había otras sesiones abiertas';
  return n === 1 ? 'Cerramos tu otra sesión abierta' : `Cerramos tus otras ${n} sesiones abiertas`;
}

/** A 429 that says how long to wait, or the catalog's sentence. */
function waitOr(err: unknown): string {
  const s = retryAfterSeconds(err);
  return s ? `Demasiados intentos. Podrás intentarlo de nuevo en ${waitText(s)}.` : authMessage(err);
}

const cancelled = (err: unknown) => errorCode(err) === REAUTH_CANCELLED;

/**
 * Own account security (admin «Mi cuenta», driver «Seguridad»): the password (change it), the
 * second factor (voluntary setup with the WhatsApp code, new recovery codes, turn off), the Google
 * account (link, unlink) and the open sessions. Must be inside a `ReauthProvider` (SessionGate
 * provides it): the sensitive operations ask for a recent confirmation of identity. The API decides
 * every rule; what is hidden here is only guidance.
 */
export function SecurityPanel({
  api,
  onSignOut,
  className = 'stack',
  layout = 'cards',
}: {
  api: ApiClient;
  onSignOut: () => void;
  /** Layout of the sections: a column by default. */
  className?: string;
  /**
   * «cards» (the panel): one card per section. «driver» (the driver app, DRV-010): flat rows edge to
   * edge under small section titles, one primary action, and «Cerrar las demás sesiones» keeps this one.
   */
  layout?: 'cards' | 'driver';
}) {
  const google = useAsync(() => api.account.google.status());
  /** Operations that close the other sessions reload the list. */
  const [sessionsKey, setSessionsKey] = useState(0);
  const reloadSessions = () => setSessionsKey((k) => k + 1);
  if (layout === 'driver')
    return (
      <div className="security-panel flat">
        <h2 className="micro-title">Ingreso</h2>
        <div className="sec-list">
          <PasswordSection api={api} hasPassword={google.data?.hasPassword} onChanged={reloadSessions} flat />
          <MfaSection api={api} onChanged={reloadSessions} flat />
          <GoogleSection api={api} status={google.data} onChanged={() => void google.reload()} flat />
        </div>
        <SessionsSection key={sessionsKey} api={api} onSignOut={onSignOut} flat />
        <SecurityNoticesSection key={`n${sessionsKey}`} api={api} flat />
      </div>
    );
  return (
    <div className={`${className} security-panel`}>
      <PasswordSection api={api} hasPassword={google.data?.hasPassword} onChanged={reloadSessions} />
      <MfaSection api={api} onChanged={reloadSessions} />
      <GoogleSection api={api} status={google.data} onChanged={() => void google.reload()} />
      <SessionsSection key={sessionsKey} api={api} onSignOut={onSignOut} />
      <SecurityNoticesSection key={`n${sessionsKey}`} api={api} />
    </div>
  );
}

// ---------------------------------------------------------------- password
function PasswordSection({ api, hasPassword, onChanged, flat }: { api: ApiClient; hasPassword: boolean | undefined; onChanged: () => void; flat?: boolean }) {
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState<number | null>(null);
  const Title = flat ? 'h3' : 'h2';
  return (
    <section className={flat ? 'sec-row' : 'card stack'} aria-labelledby="sec-password">
      <Title id="sec-password" className={flat ? 'sec-title' : 'h3'}>
        <KeyRound size={18} aria-hidden /> Contraseña
      </Title>
      <p className="small muted" style={{ margin: 0 }}>
        {flat ? 'Al cambiarla se cierran tus otras sesiones; esta sigue abierta.' : 'Con ella y tu celular o correo ingresas. Al cambiarla se cierran tus otras sesiones; esta sigue abierta.'}
      </p>
      {done !== null && (
        <Notice tone="ok" urgent>
          Contraseña cambiada. {otherSessions(done)}.
        </Notice>
      )}
      {hasPassword === false ? (
        <Notice tone="info">Tu cuenta no tiene contraseña. Para crear una, usa «¿Olvidaste tu contraseña?» en la pantalla de ingreso.</Notice>
      ) : (
        <div>
          <button
            type="button"
            className={flat ? 'btn' : 'btn sm'}
            onClick={() => {
              setDone(null);
              setOpen(true);
            }}
          >
            Cambiar contraseña
          </button>
        </div>
      )}
      {open && (
        <ChangePasswordDialog
          api={api}
          onClose={() => setOpen(false)}
          onDone={(revoked) => {
            setOpen(false);
            setDone(revoked);
            onChanged();
          }}
        />
      )}
    </section>
  );
}

/** Change the own password (reauth): the other sessions end, this one goes on. */
export function ChangePasswordDialog({ api, onClose, onDone }: { api: ApiClient; onClose: () => void; onDone: (revokedSessions: number) => void }) {
  const guard = useReauth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [currentError, setCurrentError] = useState<string | null>(null);
  const [nextError, setNextError] = useState<string | null>(null);
  const [repeatError, setRepeatError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const currentRef = useRef<HTMLInputElement>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setCurrentError(null);
    setNextError(null);
    setRepeatError(null);
    setError(null);
    if ([...next].length < PASSWORD_RULES.minLength) return setNextError(`Debe tener al menos ${PASSWORD_RULES.minLength} caracteres.`);
    if (next !== repeat) return setRepeatError('Las dos contraseñas no coinciden.');
    setBusy(true);
    try {
      const r = await guard(() => api.account.changePassword(current, next));
      onDone(r.revokedSessions);
    } catch (err) {
      if (cancelled(err)) return;
      const c = errorCode(err);
      // each field shows its own refusal (docs/design-system.md «Errores de formulario»)
      if (c === 'INVALID_PASSWORD') {
        setCurrentError(AUTH_ERRORS.INVALID_PASSWORD.user);
        currentRef.current?.focus();
      } else if (c === 'WEAK_PASSWORD') {
        const why = Array.isArray((err as ApiError).details) ? ((err as ApiError).details as Array<{ message?: unknown }>)[0]?.message : null;
        setNextError(typeof why === 'string' && why ? why : AUTH_ERRORS.WEAK_PASSWORD.user);
      } else setError(waitOr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title="Cambiar contraseña"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" form="change-password" className="btn primary" disabled={busy}>
            {busy ? 'Guardando…' : 'Cambiar contraseña'}
          </button>
        </>
      }
    >
      <form id="change-password" className="stack" onSubmit={(e) => void submit(e)}>
        <Notice tone="info">Se cerrarán tus sesiones en los demás dispositivos. Esta sesión sigue abierta.</Notice>
        <Field label="Contraseña actual" error={currentError}>
          <input
            ref={currentRef}
            className="input"
            type="password"
            value={current}
            onChange={(e) => {
              setCurrent(e.target.value);
              setCurrentError(null);
            }}
            autoComplete="current-password"
            maxLength={PASSWORD_RULES.maxLength}
            required
          />
        </Field>
        <NewPassword
          value={next}
          onChange={(v) => {
            setNext(v);
            setNextError(null);
          }}
          repeat={repeat}
          onRepeat={(v) => {
            setRepeat(v);
            setRepeatError(null);
          }}
          error={nextError}
          repeatError={repeatError}
          autoFocus={!!nextError}
        />
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- second factor
function MfaSection({ api, onChanged, flat }: { api: ApiClient; onChanged: () => void; flat?: boolean }) {
  const status = useAsync(() => api.account.mfa.status());
  const guard = useReauth();
  const confirm = useConfirm();
  const { run, busy } = useAction();
  const [enrolling, setEnrolling] = useState(false);
  const [codes, setCodes] = useState<string[] | null>(null);
  const [enabledNow, setEnabledNow] = useState(false);
  return (
    <section className={flat ? 'sec-row' : 'card stack'} aria-labelledby="sec-mfa">
      <div className="row between">
        {flat ? (
          <h3 id="sec-mfa" className="sec-title">
            <ShieldCheck size={18} aria-hidden /> Verificación en 2 pasos
          </h3>
        ) : (
          <h2 id="sec-mfa" className="h3">
            <ShieldCheck size={18} aria-hidden /> Verificación en 2 pasos
          </h2>
        )}
        {status.data &&
          (status.data.enabled ? (
            <span className="badge ok">
              {flat && <span className="dot" aria-hidden />} Activa
            </span>
          ) : (
            <span className={flat ? 'badge off' : 'badge'}>
              {flat && <span className="dot hollow" aria-hidden />} Inactiva
            </span>
          ))}
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        {flat
          ? 'Además de la contraseña, se pide un código que cambia cada 30 segundos. Protege tu cuenta aunque alguien conozca tu contraseña.'
          : 'Además de la contraseña, al ingresar se pide un código que cambia cada 30 segundos en tu teléfono. Protege tu cuenta aunque alguien conozca tu contraseña.'}
      </p>
      {enabledNow && (
        <Notice tone="ok" urgent>
          Verificación en 2 pasos activada. Por seguridad cerramos tus otras sesiones abiertas.
        </Notice>
      )}
      <Loadable state={status}>
        {(o) => (
          <>
            {o.required && o.enabled && <Notice tone="info">Es obligatoria para tu cuenta: no se puede desactivar.</Notice>}
            {o.enabled ? (
              <>
                {o.recoveryCodesRemaining <= 3 && (
                  <Notice tone="warn">Te quedan {o.recoveryCodesRemaining} código(s) de recuperación. Genera nuevos para no quedarte sin acceso si pierdes el teléfono.</Notice>
                )}
                <div className="row">
                  <button
                    type="button"
                    className="btn sm"
                    disabled={busy}
                    onClick={async () => {
                      if (!(await confirm({ title: 'Generar códigos nuevos', message: 'Los códigos de recuperación anteriores dejarán de servir.', confirmLabel: 'Generar' }))) return;
                      const r = await run(() => guard(() => api.account.mfa.recoveryCodes()));
                      if (r) setCodes(r.recoveryCodes);
                    }}
                  >
                    Nuevos códigos de recuperación ({o.recoveryCodesRemaining} disponibles)
                  </button>
                  {!o.required && (
                    <button
                      type="button"
                      className="btn sm danger-outline"
                      disabled={busy}
                      onClick={async () => {
                        const ok = await confirm({
                          title: 'Desactivar la verificación en 2 pasos',
                          message: 'Tu cuenta quedará protegida solo por la contraseña y se cerrarán tus otras sesiones.',
                          danger: true,
                          confirmLabel: 'Desactivar',
                        });
                        if (ok && (await run(() => guard(() => api.account.mfa.disable()), 'Verificación en 2 pasos desactivada')) !== undefined) {
                          setEnabledNow(false);
                          void status.reload();
                          onChanged();
                        }
                      }}
                    >
                      Desactivar
                    </button>
                  )}
                </div>
              </>
            ) : o.required ? (
              // the policy demands it and there is none: only an invitation sets it up (SEC-E1-1)
              <Notice tone="warn">{AUTH_ERRORS.MFA_ENROLLMENT_BLOCKED.user}</Notice>
            ) : (
              <div>
                {/* the one primary action of the driver's view: full width (DRV-010) */}
                <button type="button" className={flat ? 'btn primary block' : 'btn primary sm'} onClick={() => setEnrolling(true)}>
                  Activar verificación en 2 pasos
                </button>
              </div>
            )}
          </>
        )}
      </Loadable>
      {enrolling && (
        <EnrollDialog
          api={api}
          onClose={() => setEnrolling(false)}
          onDone={(c) => {
            setEnrolling(false);
            setCodes(c);
            setEnabledNow(true);
            void status.reload();
            onChanged();
          }}
        />
      )}
      {codes && (
        <Modal title="Códigos de recuperación" onClose={() => undefined}>
          <RecoveryCodes
            codes={codes}
            doneLabel="Listo"
            onDone={() => {
              setCodes(null);
              void status.reload();
            }}
          />
        </Modal>
      )}
    </section>
  );
}

/** Wait this long before offering another WhatsApp code (3 an hour per account). */
const RESEND_AFTER_S = 60;

/** Why the setup cannot start (or go on) from this session. */
function blockedText(err: unknown): string | null {
  const c = errorCode(err);
  if (c === 'MFA_ENROLL_BLOCKED') {
    const at = ((err as ApiError).details as { retryAt?: unknown } | undefined)?.retryAt;
    return typeof at === 'string' ? `Recuperaste tu contraseña hace poco. Por seguridad podrás activar la verificación en 2 pasos el ${when(at)}.` : AUTH_ERRORS.MFA_ENROLL_BLOCKED.user;
  }
  if (c === 'PHONE_NOT_VERIFIED') return `${AUTH_ERRORS.PHONE_NOT_VERIFIED.user} El código de activación se envía a tu celular verificado.`;
  if (c === 'MFA_ENROLLMENT_BLOCKED') return AUTH_ERRORS.MFA_ENROLLMENT_BLOCKED.user;
  if (c === 'MFA_ALREADY_ENABLED') return 'Tu verificación en 2 pasos ya está activa.';
  if (c === 'PHONE_REQUIRES_UPDATE') return 'El celular de tu cuenta es provisional y no puede recibir el código. Pide al administrador de tu empresa que lo corrija.';
  return null;
}

/**
 * Voluntary setup from a session (docs/authentication.md «Alta de 2FA con código»): the QR of the
 * authenticator app → a code sent to the account's WhatsApp → both codes → the recovery codes,
 * shown once. Each step may ask for a recent confirmation of identity.
 */
export function EnrollDialog({ api, onClose, onDone }: { api: ApiClient; onClose: () => void; onDone: (codes: string[]) => void }) {
  const guard = useReauth();
  const [step, setStep] = useState<'qr' | 'codes'>('qr');
  const [setup, setSetup] = useState<MfaSetup | null>(null);
  const [blocked, setBlocked] = useState<string | null>(null);
  const [phoneMasked, setPhoneMasked] = useState<string | null>(null);
  const [sentAt, setSentAt] = useState(0);
  const [otp, setOtp] = useState('');
  const [code, setCode] = useState('');
  const [otpError, setOtpError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const now = useNow(1000);
  const waitS = Math.max(0, Math.ceil((sentAt + RESEND_AFTER_S * 1000 - now) / 1000));

  /** A refusal that ends the setup, or a message for this step. */
  const fail = (err: unknown) => {
    if (cancelled(err)) return;
    const b = blockedText(err);
    if (b) setBlocked(b);
    else setError(waitOr(err));
  };

  // once (StrictMode runs effects twice: a second enroll would replace the secret on screen)
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    guard(() => api.account.mfa.enroll()).then(setSetup, (err) => (cancelled(err) ? onClose() : fail(err)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const sendCode = async (again: boolean) => {
    setBusy(true);
    setError(null);
    setOtpError(null);
    setNotice(null);
    try {
      const r = await guard(() => api.account.mfa.sendCode());
      setPhoneMasked(r.phoneMasked);
      setSentAt(Date.now());
      setOtp('');
      if (again) setNotice('Te enviamos un código nuevo. El anterior ya no sirve.');
      setStep('codes');
    } catch (err) {
      const s = retryAfterSeconds(err);
      if (s) {
        setSentAt(Date.now() + s * 1000 - RESEND_AFTER_S * 1000);
        setError(`Ya se pidieron varios códigos. Podrás pedir otro en ${waitText(s)}.`);
      } else fail(err);
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    setError(null);
    setOtpError(null);
    setCodeError(null);
    try {
      const r = await guard(() => api.account.mfa.confirm(code, otp));
      onDone(r.recoveryCodes);
    } catch (err) {
      const c = errorCode(err);
      if (c === 'MFA_INVALID') {
        // the app's code was wrong: the WhatsApp code is not spent, only this one is typed again
        setCode('');
        setCodeError(`${AUTH_ERRORS.MFA_INVALID.user} Escribe el código que muestra ahora la app.`);
      } else if (c === 'OTP_INVALID') {
        const left = ((err as ApiError).details as { attemptsLeft?: unknown } | undefined)?.attemptsLeft;
        setOtp('');
        setOtpError(`${AUTH_ERRORS.OTP_INVALID.user}${typeof left === 'number' ? (left === 1 ? ' Te queda 1 intento con este código.' : ` Te quedan ${left} intentos con este código.`) : ''}`);
      } else if (c === 'OTP_EXPIRED' || c === 'OTP_TOO_MANY_ATTEMPTS') {
        // this WhatsApp code is dead: another one can be requested right away
        setOtp('');
        setSentAt(0);
        setOtpError(authMessage(err));
      } else fail(err);
    } finally {
      setBusy(false);
    }
  };

  const footer = blocked ? (
    <button type="button" className="btn" onClick={onClose}>
      Cerrar
    </button>
  ) : (
    <>
      <button type="button" className="btn" onClick={onClose}>
        Cancelar
      </button>
      {step === 'qr' ? (
        <button type="submit" form="enroll-mfa" className="btn primary" disabled={busy || !setup}>
          <MessageCircle size={16} aria-hidden /> {busy ? 'Enviando…' : 'Continuar'}
        </button>
      ) : (
        <button type="submit" form="enroll-mfa" className="btn primary" disabled={busy || otp.length !== 6 || code.length !== 6}>
          {busy ? 'Verificando…' : 'Activar'}
        </button>
      )}
    </>
  );

  return (
    <Modal title="Activar verificación en 2 pasos" onClose={onClose} footer={footer}>
      <form
        id="enroll-mfa"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (blocked || busy) return;
          void (step === 'qr' ? sendCode(false) : activate());
        }}
      >
        {blocked ? (
          <Notice tone="warn" urgent>
            {blocked}
          </Notice>
        ) : !setup ? (
          error ? null : <Spinner block label="Preparando" />
        ) : step === 'qr' ? (
          <>
            <p className="small muted" style={{ margin: 0 }}>
              Paso 1 de 2 · Agrega tu cuenta a una app de autenticación. Después te enviaremos un código a tu WhatsApp para confirmar que eres tú.
            </p>
            <AuthenticatorSetup {...setup} />
          </>
        ) : (
          <>
            <p className="small muted" style={{ margin: 0 }}>
              Paso 2 de 2 · Escribe los dos códigos.
            </p>
            <Notice tone="info">
              {notice ?? (
                <>
                  Enviamos un código de 6 dígitos al WhatsApp de <b className="nowrap">{phoneMasked}</b>.
                </>
              )}{' '}
              Vence en 10 minutos.
            </Notice>
            <CodeInput
              value={otp}
              onChange={(v) => {
                setOtp(v);
                setOtpError(null);
              }}
              autoFocus={!codeError}
              label="Código de WhatsApp"
              hint="Los 6 dígitos del mensaje que acabas de recibir."
              error={otpError}
            />
            <CodeInput
              value={code}
              onChange={(v) => {
                setCode(v);
                setCodeError(null);
              }}
              autoFocus={!!codeError}
              label="Código de la app"
              hint="Los 6 dígitos que muestra ahora tu app de autenticación."
              error={codeError}
            />
            <div className="row">
              <button type="button" className="btn ghost sm" disabled={busy || waitS > 0} onClick={() => void sendCode(true)}>
                Enviar otro código
              </button>
              <button type="button" className="btn ghost sm" onClick={() => setStep('qr')}>
                Ver el código QR de nuevo
              </button>
            </div>
            {waitS > 0 && (
              <p className="small muted" style={{ margin: 0 }} role="status">
                ¿No te llegó? Podrás pedir otro en {waitS > 90 ? waitText(waitS) : countdown(waitS)}.
              </p>
            )}
          </>
        )}
        {error && (
          <div className="error-box" role="alert">
            {error}
          </div>
        )}
      </form>
    </Modal>
  );
}

// ---------------------------------------------------------------- Google
function GoogleSection({ api, status, onChanged, flat }: { api: ApiClient; status: GoogleAccountStatus | null; onChanged: () => void; flat?: boolean }) {
  const guard = useReauth();
  const confirm = useConfirm();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // nothing to offer: the company has no sign-in with Google and the account has none linked
  if (!status || (!status.available && !status.linked)) return null;

  const link = async () => {
    setBusy(true);
    setError(null);
    try {
      const { url } = await guard(() => api.account.google.link());
      // the page unloads: come back here after confirming the account at /acceso/google
      saveReturnPath(`${window.location.pathname}${window.location.search}`);
      goToGoogle(url);
    } catch (err) {
      if (!cancelled(err)) setError(authMessage(err));
      setBusy(false);
    }
  };
  const unlink = async () => {
    const ok = await confirm({
      title: 'Desvincular tu cuenta de Google',
      message: 'Ya no podrás ingresar con Google. Se cerrarán las sesiones que abriste con Google, también esta si ingresaste con Google: en ese caso tendrás que ingresar de nuevo con tu contraseña.',
      danger: true,
      confirmLabel: 'Desvincular',
    });
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const r = await guard(() => api.account.google.unlink());
      toast(r.revokedSessions ? `Cuenta de Google desvinculada. Se cerraron ${r.revokedSessions} sesión(es) abiertas con Google.` : 'Cuenta de Google desvinculada');
      onChanged();
    } catch (err) {
      if (!cancelled(err)) setError(authMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={flat ? 'sec-row' : 'card stack'} aria-labelledby="sec-google">
      <div className="row between">
        <h2 id="sec-google" className="h3">
          Ingreso con Google
        </h2>
        {status.linked ? <span className="badge ok">Vinculada</span> : <span className="badge">Sin vincular</span>}
      </div>
      {status.linked ? (
        <>
          <p className="small" style={{ margin: 0 }}>
            Puedes ingresar con {status.email ? <b className="nowrap">{status.email}</b> : 'tu cuenta de Google'}
            {status.linkedAt ? `, vinculada el ${when(status.linkedAt)}` : ''}. Si tienes la verificación en 2 pasos, también se te pide al ingresar con Google.
          </p>
          {!status.available && <Notice tone="info">Tu empresa desactivó el ingreso con Google: por ahora ingresa con tu contraseña. Puedes desvincular la cuenta.</Notice>}
          {status.hasPassword ? (
            <div>
              <button type="button" className="btn sm danger-outline" disabled={busy} onClick={() => void unlink()}>
                Desvincular Google
              </button>
            </div>
          ) : (
            <Notice tone="warn">{AUTH_ERRORS.PASSWORD_REQUIRED.user}</Notice>
          )}
        </>
      ) : (
        <>
          <p className="small muted" style={{ margin: 0 }}>
            Ingresa con tu cuenta de Google en lugar de escribir la contraseña. Saldrás un momento a Google para elegir la cuenta y volverás aquí para confirmarla.
          </p>
          <div>
            <button type="button" className="btn sm" disabled={busy} onClick={() => void link()}>
              {busy ? 'Abriendo Google…' : 'Vincular cuenta de Google'}
            </button>
          </div>
        </>
      )}
      {error && (
        <div className="error-box" role="alert">
          {error}
        </div>
      )}
    </section>
  );
}

// ---------------------------------------------------------------- sessions
function SessionsSection({ api, onSignOut, flat }: { api: ApiClient; onSignOut: () => void; flat?: boolean }) {
  const sessions = useAsync(() => api.get<OwnSession[]>('/auth/sessions'));
  const confirm = useConfirm();
  const { run, busy } = useAction();
  const others = sessions.data?.filter((s) => !s.current).length ?? 0;
  if (flat) return <SessionsFlat api={api} sessions={sessions} others={others} />;
  return (
    <section className="card stack" aria-labelledby="sec-sessions">
      <div className="row between">
        <h2 id="sec-sessions" className="h3">
          Sesiones abiertas
        </h2>
        {sessions.data && (
          <button
            type="button"
            className="btn sm danger-outline"
            disabled={busy}
            onClick={async () => {
              const ok = await confirm({
                title: 'Cerrar todas las sesiones',
                message: `Se cerrarán ${others ? `tus ${others + 1} sesiones, también esta` : 'esta sesión'}. Tendrás que ingresar de nuevo en cada dispositivo.`,
                danger: true,
                confirmLabel: 'Cerrar todas',
              });
              // success ends this session too: the app goes back to the sign-in screen
              if (ok) await run(() => api.logoutAll());
            }}
          >
            Cerrar todas
          </button>
        )}
      </div>
      <p className="small muted" style={{ margin: 0 }}>
        Si ves un dispositivo que no reconoces, ciérralo y avisa al administrador de tu empresa.
      </p>
      <Loadable state={sessions}>
        {(list) =>
          list.length === 0 ? (
            <p className="small muted">No hay sesiones abiertas.</p>
          ) : (
            <ul className="session-list" aria-label="Sesiones abiertas">
              {list.map((s) => (
                <li key={s.id}>
                  {s.platform === 'PWA' || /Android|iPhone/i.test(s.user_agent ?? '') ? <Smartphone size={18} aria-hidden /> : <Laptop size={18} aria-hidden />}
                  <span className="session-text">
                    <b>{deviceName(s)}</b> {s.current && <span className="badge ok">Esta sesión</span>}
                    <span className="small muted session-meta">
                      {APP_NAME[s.audience] ?? s.audience} · última actividad {dateTime(s.last_used_at)}
                      {s.ip ? ` · ${s.ip}` : ''}
                    </span>
                  </span>
                  {s.current ? (
                    <button type="button" className="btn sm" onClick={onSignOut}>
                      <LogOut size={14} aria-hidden /> Salir
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn sm"
                      disabled={busy}
                      aria-label={`Cerrar la sesión de ${deviceName(s)}`}
                      onClick={() => void run(() => api.del(`/auth/sessions/${s.id}`), 'Sesión cerrada').then(() => sessions.reload())}
                    >
                      Cerrar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Loadable>
    </section>
  );
}

/**
 * The driver's sessions (DRV-010): rows edge to edge, «Cerrar» only on the others, and «Cerrar las
 * demás sesiones» closes each other session (DELETE /auth/sessions/:id): this one stays open.
 */
function SessionsFlat({ api, sessions, others }: { api: ApiClient; sessions: ReturnType<typeof useAsync<OwnSession[]>>; others: number }) {
  const confirm = useConfirm();
  const { run, busy } = useAction();
  const closeOthers = async () => {
    const ok = await confirm({
      title: 'Cerrar las demás sesiones',
      message: `Se cerrará${others === 1 ? ' la sesión' : `n las ${others} sesiones`} de tus otros dispositivos. Esta sigue abierta.`,
      danger: true,
      confirmLabel: 'Cerrar las demás',
    });
    if (!ok) return;
    const ids = (sessions.data ?? []).filter((x) => !x.current).map((x) => x.id);
    await run(async () => {
      for (const id of ids) await api.del(`/auth/sessions/${id}`);
    }, ids.length === 1 ? 'Sesión cerrada' : 'Sesiones cerradas');
    void sessions.reload();
  };
  return (
    <section aria-labelledby="sec-sessions">
      <h2 id="sec-sessions" className="micro-title">
        Sesiones abiertas {sessions.data && <span className="mono">{sessions.data.length}</span>}
      </h2>
      <Loadable state={sessions}>
        {(list) =>
          list.length === 0 ? (
            <p className="sec-empty">No hay sesiones abiertas.</p>
          ) : (
            <ul className="session-rows" aria-label="Sesiones abiertas">
              {list.map((x) => (
                <li key={x.id}>
                  {x.platform === 'PWA' || /Android|iPhone/i.test(x.user_agent ?? '') ? <Smartphone size={20} aria-hidden /> : <Laptop size={20} aria-hidden />}
                  <span className="session-text">
                    <b>{deviceName(x)}</b>
                    {x.current && (
                      <span className="badge ok">
                        <span className="dot" aria-hidden /> Esta sesión
                      </span>
                    )}
                    <span className="session-meta">
                      {APP_NAME[x.audience] ?? x.audience} · <span className="mono">{dateTime(x.last_used_at)}</span>
                      {x.ip && (
                        <>
                          {' · '}
                          <span className="mono">{x.ip}</span>
                        </>
                      )}
                    </span>
                  </span>
                  {!x.current && (
                    <button
                      type="button"
                      className="btn"
                      disabled={busy}
                      aria-label={`Cerrar la sesión de ${deviceName(x)}`}
                      onClick={() => void run(() => api.del(`/auth/sessions/${x.id}`), 'Sesión cerrada').then(() => sessions.reload())}
                    >
                      Cerrar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )
        }
      </Loadable>
      <div className="sec-after">
        <p className="small muted">Si ves un dispositivo que no reconoces, ciérralo y avisa al administrador de tu empresa.</p>
        {others > 0 && (
          <button type="button" className="btn block close-others" disabled={busy} onClick={() => void closeOthers()}>
            Cerrar las demás sesiones
          </button>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- security notices
/** What each security notice is about (`data.kind`), as a short label next to its title. */
export const SECURITY_NOTICE_LABEL: Record<string, string> = {
  PASSWORD_CHANGED: 'Cambio de contraseña',
  PASSWORD_RESET: 'Recuperación de contraseña',
  MEMBER_PASSWORD_RESET: 'Un usuario recuperó su contraseña',
  PHONE_CHANGED: 'Cambio de celular',
  MFA_RESET: 'Verificación en 2 pasos restablecida',
  GOOGLE_LINKED: 'Google vinculado',
  GOOGLE_UNLINKED: 'Google desvinculado',
  // the company's daily budget of WhatsApp codes reached 80 % (owners only); the title carries the figure
  OTP_BUDGET: 'Cupo de códigos por WhatsApp',
};

/** The label of a security notice; the generic one for kinds this app does not know yet. */
export const securityNoticeLabel = (n: Pick<InboxNotification, 'data'>) =>
  // the same alert exists for the budget of security notices (`data.budget`)
  n.data?.kind === 'OTP_BUDGET' && n.data.budget === 'whatsapp_notice' ? 'Cupo de avisos por WhatsApp' : (SECURITY_NOTICE_LABEL[String(n.data?.kind ?? '')] ?? 'Aviso de seguridad');

const SHOWN_NOTICES = 10;

/**
 * Recent security notices of the inbox (category SECURITY): changes to this account's credentials,
 * for whoever manages people, changes to theirs (`MEMBER_PASSWORD_RESET`, `PHONE_CHANGED`,
 * `MFA_RESET`) and, for the owners, the daily budget of WhatsApp codes running out (`OTP_BUDGET`).
 * The text comes from the API; it never carries a secret, a phone or an email.
 */
function SecurityNoticesSection({ api, flat }: { api: ApiClient; flat?: boolean }) {
  const inbox = useAsync(() => api.notifications.inbox(50));
  if (flat)
    return (
      <section aria-labelledby="sec-notices">
        <h2 id="sec-notices" className="micro-title">
          Avisos de seguridad
        </h2>
        <Loadable state={inbox}>
          {(all) => {
            const list = all.filter((n) => n.category === 'SECURITY').slice(0, SHOWN_NOTICES);
            return list.length === 0 ? (
              <p className="sec-empty ok">
                <CircleCheck size={18} aria-hidden /> No hay avisos de seguridad recientes.
              </p>
            ) : (
              <ol className="timeline" aria-label="Avisos de seguridad">
                {list.map((n) => (
                  <li key={n.id}>
                    <span className="badge warn">{securityNoticeLabel(n)}</span> <b>{n.title}</b>
                    {n.body && <span className="session-meta">{n.body}</span>}
                    <span className="session-meta mono">{dateTime(n.created_at)}</span>
                  </li>
                ))}
              </ol>
            );
          }}
        </Loadable>
      </section>
    );
  return (
    <section className="card stack" aria-labelledby="sec-notices">
      <h2 id="sec-notices" className="h3">
        <BellRing size={18} aria-hidden /> Avisos de seguridad
      </h2>
      <p className="small muted" style={{ margin: 0 }}>
        Te avisamos cuando cambian las credenciales de tu cuenta y, si administras personas, las de tu equipo. Si no reconoces un cambio, avisa al administrador de tu empresa.
      </p>
      <Loadable state={inbox}>
        {(all) => {
          const list = all.filter((n) => n.category === 'SECURITY').slice(0, SHOWN_NOTICES);
          return list.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>
              No hay avisos de seguridad recientes.
            </p>
          ) : (
            <ul className="security-events" aria-label="Avisos de seguridad">
              {list.map((n) => (
                <li key={n.id}>
                  <span>
                    <span className="badge warn">{securityNoticeLabel(n)}</span> <b>{n.title}</b>
                    {n.body && <span className="small session-meta">{n.body}</span>}
                  </span>
                  <span className="small muted nowrap">{dateTime(n.created_at)}</span>
                </li>
              ))}
            </ul>
          );
        }}
      </Loadable>
    </section>
  );
}
